import { Component, HostListener, OnInit } from '@angular/core';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { ServiceMedia } from '../../services/service-provider.service';
import { FileService } from '../../services/file.service';

// Config passed via DynamicDialogConfig.data.
export interface MediaViewerModalData {
  title?: string;
  kind: 'document' | 'image';
  items: ServiceMedia[]; // pre-loaded media rows
  emptyMessage?: string;
}

@Component({
  selector: 'app-media-viewer',
  templateUrl: './media-viewer.component.html',
  styleUrls: ['./media-viewer.component.scss'],
})
export class MediaViewerComponent implements OnInit {
  data!: MediaViewerModalData;

  // Resolved fresh preview URLs per image id (signed URLs expire on every fetch).
  imageUrls: Record<string, string> = {};
  // Natural pixel dimensions per image, captured on load.
  imageDims: Record<string, { w: number; h: number }> = {};
  loadingImages = false;

  // Active preview state — index of the item whose preview is open, or null.
  previewIndex: number | null = null;
  previewUrl: string | null = null;
  previewLoading = false;

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private fileService: FileService
  ) {}

  ngOnInit(): void {
    const incoming = this.config.data as MediaViewerModalData;
    this.data = {
      ...incoming,
      kind: incoming?.kind ?? 'document',
      items: incoming?.items ?? [],
    };

    // For galleries, resolve a fresh preview link per image (signed URLs expire).
    if (this.data.kind === 'image' && this.data.items.length) {
      this.loadingImages = true;
      let pending = this.data.items.length;
      for (const item of this.data.items) {
        this.fileService.getPreviewLink(String(item.fileId)).subscribe({
          next: (url) => {
            this.imageUrls[item.id] = url;
            if (--pending === 0) this.loadingImages = false;
          },
          error: () => {
            if (--pending === 0) this.loadingImages = false;
          },
        });
      }
    }
  }

  // Capture an image's natural pixel size once it loads.
  onImageLoad(item: ServiceMedia, ev: Event): void {
    const img = ev.target as HTMLImageElement;
    if (img?.naturalWidth) {
      this.imageDims[item.id] = { w: img.naturalWidth, h: img.naturalHeight };
    }
  }

  // Works for documents and images: fetch a fresh download link, then open it.
  download(item: ServiceMedia): void {
    this.fileService.getDownloadLink(String(item.fileId)).subscribe((url) => {
      if (url) window.open(url, '_blank');
    });
  }

  fileName(item: ServiceMedia): string {
    return item._file?.name ?? `File ${item.fileId}`;
  }

  // ---------- File-type helpers (used by the icon + viewable gate) -------------

  isPdf(item: ServiceMedia): boolean {
    const type = (item._file?.type || '').toLowerCase();
    const name = (item._file?.name || '').toLowerCase();
    return type.includes('pdf') || name.endsWith('.pdf');
  }

  isImage(item: ServiceMedia): boolean {
    const type = (item._file?.type || '').toLowerCase();
    return type.includes('image');
  }

  // Whether we can render an inline preview at all (PDF or image).
  isViewable(item: ServiceMedia): boolean {
    return this.isPdf(item) || this.isImage(item);
  }

  // PrimeIcon class matching the file type. PrimeIcons 3+ ships the file-* variants
  // we use here; falls back to a generic file icon.
  iconClass(item: ServiceMedia): string {
    const type = (item._file?.type || '').toLowerCase();
    const name = (item._file?.name || '').toLowerCase();
    if (type.includes('image')) return 'pi pi-image';
    if (type.includes('pdf') || name.endsWith('.pdf')) return 'pi pi-file-pdf';
    if (
      type.includes('sheet') ||
      type.includes('excel') ||
      name.endsWith('.xlsx') ||
      name.endsWith('.xls') ||
      name.endsWith('.csv')
    )
      return 'pi pi-file-excel';
    if (
      type.includes('word') ||
      name.endsWith('.docx') ||
      name.endsWith('.doc')
    )
      return 'pi pi-file-word';
    if (type.includes('video') || name.endsWith('.mp4') || name.endsWith('.mov'))
      return 'pi pi-video';
    return 'pi pi-file';
  }

  // ---------- Preview dialog ---------------------------------------------------

  openPreview(index: number): void {
    if (index < 0 || index >= this.data.items.length) return;
    const item = this.data.items[index];
    if (!this.isViewable(item)) return; // Other file types just download.

    this.previewIndex = index;

    // Images: reuse the already-loaded thumbnail URL when available.
    if (this.isImage(item) && this.imageUrls[item.id]) {
      this.previewUrl = this.imageUrls[item.id];
      return;
    }

    // Otherwise resolve a fresh preview link (PDF iframe, image we hadn't loaded yet).
    this.previewUrl = null;
    this.previewLoading = true;
    this.fileService.getPreviewLink(String(item.fileId)).subscribe({
      next: (url) => {
        this.previewLoading = false;
        if (this.previewIndex === index) this.previewUrl = url;
      },
      error: () => {
        this.previewLoading = false;
      },
    });
  }

  closePreview(): void {
    this.previewIndex = null;
    this.previewUrl = null;
    this.previewLoading = false;
  }

  previewNext(): void {
    if (this.previewIndex === null) return;
    this.openPreview((this.previewIndex + 1) % this.data.items.length);
  }

  previewPrev(): void {
    if (this.previewIndex === null) return;
    const n = this.data.items.length;
    this.openPreview((this.previewIndex - 1 + n) % n);
  }

  get previewItem(): ServiceMedia | null {
    return this.previewIndex !== null ? this.data.items[this.previewIndex] ?? null : null;
  }

  // Esc closes the preview, arrows navigate. Bound on the host so teardown follows
  // the component.
  @HostListener('window:keydown', ['$event'])
  onKeydown(ev: KeyboardEvent): void {
    if (this.previewIndex === null) return;
    if (ev.key === 'Escape') {
      ev.preventDefault();
      this.closePreview();
    } else if (ev.key === 'ArrowRight') {
      ev.preventDefault();
      this.previewNext();
    } else if (ev.key === 'ArrowLeft') {
      ev.preventDefault();
      this.previewPrev();
    }
  }

  close(): void {
    this.ref.close();
  }
}
